import React, { useCallback, useState, useEffect } from 'react';
import { isPersonalLinkedSession } from '../utils/webHost';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { logout, setTokenGetter, setEntrepriseIdGetter } from '../utils/api';
import { getEntrepriseIdFromUser, readJwtPayload } from '../utils/userIdentity';
import useIdleLogout from '../hooks/useIdleLogout';

const AuthContext = React.createContext();

export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  // JSX visible immédiatement pour forcer la détection
  const _jsxCheck = <div style={{ display: 'none' }} />;

  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setTokenGetter(() => token);
    setEntrepriseIdGetter(() => user?.entreprise_id);
  }, [token, user]);

  useEffect(() => {
    const checkAuth = () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          const payload = JSON.parse(atob(storedToken.split('.')[1]));
          const now = Math.floor(Date.now() / 1000);
          const isExpired = payload.exp < now;

          if (isExpired) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null);
            setUser(null);
          } else {
            const userData = JSON.parse(storedUser);
            const jwtPayload = readJwtPayload(storedToken);
            const entrepriseId =
              getEntrepriseIdFromUser(userData) ??
              jwtPayload?.entreprise_id ??
              jwtPayload?.entrepriseId ??
              null;
            const normalizedUser = entrepriseId
              ? { ...userData, entreprise_id: userData.entreprise_id ?? entrepriseId }
              : userData;
            setToken(storedToken);
            setUser(normalizedUser);

            setTokenGetter(() => storedToken);
            setEntrepriseIdGetter(() => normalizedUser.entreprise_id ?? entrepriseId);
          }
        } catch (error) {
          console.error('❌ Erreur lors du parsing du token:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      }

      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = (userData, userToken) => {
    setUser(userData);
    setToken(userToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', userToken);
    setTokenGetter(() => userToken);
    setEntrepriseIdGetter(() => userData.entreprise_id);
  };

  const handleLogout = useCallback(
    async (options = {}) => {
      const { reason } = options;
      try {
        await logout();
      } catch (error) {
        console.error('Erreur lors de la déconnexion:', error);
      } finally {
        try {
          if (typeof sessionStorage !== 'undefined') {
            Object.keys(sessionStorage)
              .filter((key) => key.startsWith('secura_payroll_gate_'))
              .forEach((key) => sessionStorage.removeItem(key));
          }
        } catch {
          /* ignore */
        }
        setUser(null);
        setToken(null);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        setTokenGetter(() => null);
        setEntrepriseIdGetter(() => null);
        if (reason === 'idle') {
          toast.error('Session expirée pour inactivité (15 min).');
        }
        navigate('/login');
      }
    },
    [navigate]
  );

  useIdleLogout({
    // Ordinateur personnel connecté par QR code : reste connecté (révocable depuis le téléphone)
    enabled: Boolean(token && user) && !isPersonalLinkedSession(token),
    timeoutMs: 15 * 60 * 1000,
    onIdle: () => handleLogout({ reason: 'idle' })
  });

  const isAuthenticated = () => !!token && !!user;

  const getEntrepriseId = () => user?.entreprise_id;

  const value = {
    user,
    token,
    loading,
    isAuthenticated,
    handleLogin,
    handleLogout,
    getEntrepriseId
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
